import { PDFDocument } from 'pdf-lib';

/** 一度に抽出できる最大ページ数 (旧 backend page_filter.py の上限を frontend へ移設、issue #113 案C)。 */
export const MAX_EXTRACT_PAGES = 50;

/**
 * 選択ページのみを抽出した PDF Blob を返す (issue #113 案C、mikuru3 #51 PoC 昇格)。
 * - selectedPages が空 → 元 file をそのまま返す (未選択 = 全ページ、抽出しない)。
 * - 選択が全ページを被覆 → 元 file を返す (抽出不要)。
 * - 部分選択 → pdf-lib で該当ページを copyPages して新 PDF Blob を返す。
 * - 選択ページ数が {@link MAX_EXTRACT_PAGES} を超える場合は Error。
 *
 * selectedPages は 1-origin のページ番号集合。
 * issue #69: totalPages を渡すと PDFDocument.load (blocking I/O) の前に「全ページ被覆 / 上限超」を
 * 判定して無駄な load を省く (全選択が既定なので大半のケースで load を回避できる)。 totalPages が
 * 未知 (undefined / 0) のときは従来どおり load 後に総ページ数を取って判定する (安全側で挙動を維持)。
 */
export async function extractSelectedPages(
  file: File,
  selectedPages: Set<number>,
  totalPages?: number,
): Promise<Blob> {
  const count = selectedPages.size;
  if (count === 0) return file; // 未選択 = 全ページ
  // issue #69: totalPages 既知なら load 前に判定して blocking I/O を省く。
  if (totalPages !== undefined && totalPages > 0) {
    if (count >= totalPages) return file; // 全ページ被覆 = 抽出不要 (上限は無関係)
    if (count > MAX_EXTRACT_PAGES) throw pageLimitError(count);
  }

  const bytes = await file.arrayBuffer();
  const src = await PDFDocument.load(bytes);
  const total = src.getPageCount();
  if (count >= total) return file; // 全ページ被覆 = 抽出不要 (totalPages 未知/不一致の保険、上限は無関係)
  if (count > MAX_EXTRACT_PAGES) throw pageLimitError(count);

  const indices = Array.from(selectedPages).sort((a, b) => a - b).map((p) => p - 1); // 1-origin → 0-origin
  const out = await PDFDocument.create();
  const copied = await out.copyPages(src, indices);
  copied.forEach((pg) => out.addPage(pg));
  const outBytes = await out.save();
  // issue #131: name 付き File で返す (name 無し Blob だと multipart filename が "blob" に化け、
  // backend 自動保存が blob.omfz / blob.pdf を採用してしまう)。 元 file.name を引き継ぐ。
  return new File([outBytes], file.name, { type: 'application/pdf' });
}

function pageLimitError(count: number): Error {
  return new Error(`選択ページ数 ${count} が上限 ${MAX_EXTRACT_PAGES} を超えています`);
}
